import * as THREE from 'three';
import { TERRAIN, CLOUDS } from '../core/SimConfig.js';
import TerrainTextureCache from './TerrainTextureCache.js';
import cloudShadowPars from '../../glsl/cloud_shadow_pars.glsl';

/**
 * Chunked quadtree terrain with distance-based LOD.
 *
 *   - Fixed grid of tiles (TERRAIN.cols × TERRAIN.rows), each TERRAIN.tileSize
 *     metres on a side, centred on TERRAIN.origin.
 *   - A quadtree over the grid accelerates frustum / distance culling.
 *   - Each visible tile is rendered as a single PlaneGeometry whose
 *     subdivision count is picked from TERRAIN.lodRings based on the tile's
 *     distance to the camera.
 *   - Geometries are shared per LOD level (all tiles at LOD i reuse one
 *     PlaneGeometry). Checkpoint 2 will replace shared geometries with
 *     per-tile displaced ones.
 *   - Debug mode renders tiles as a wireframe coloured by LOD level;
 *     toggled at runtime via `toggleDebug()`.
 */

const _frustum = new THREE.Frustum();
const _projScreenMatrix = new THREE.Matrix4();
const _box = new THREE.Box3();
const _camPos = new THREE.Vector3();

// Wireframe colours per LOD ring (repeats if there are more rings than colours)
const LOD_DEBUG_COLORS = [0xff3344, 0xffaa00, 0x33dd44, 0x3388ff, 0xaa44ff];

export default class Terrain {
  constructor({ anisotropy = 1, cloudShadowUniforms = null } = {}) {
    this.group = new THREE.Group();
    this.group.name = 'Terrain';

    this.debug = TERRAIN.debugWireframe;

    // ── Cloud shadows ────────────────────────────────────────────────────
    // Optional shared uniform block (from CloudLayer). When present, every
    // terrain material is patched to sample the deck's density along the sun
    // ray and darken the lit surface — a soft, drifting cloud shadow. The
    // octave/step counts are baked as #defines from SimConfig.
    // The step count is also the one the cloud quality preset drives, so it's
    // kept as its own field and folded into the program cache key — see
    // `setCloudShadowSteps()`.
    this._cloudShadowUniforms = cloudShadowUniforms;
    this._cloudShadowSteps = Math.max(1, CLOUDS.shadow.steps | 0);
    this._cloudShadowDefines = this._buildCloudShadowDefines();

    // ── Streaming cache for height / colour tile data ───────────────────
    this._cache = new TerrainTextureCache({
      maxSize: TERRAIN.textureCacheSize,
      basePath: `${import.meta.env.BASE_URL}assets/`,
      anisotropy,
    });

    // ── LRU of recently-displaced geometries ────────────────────────────
    // Tiles that leave view stash their geometry here instead of disposing
    // it. Reappearing tiles take from the cache, skipping a heightmap decode
    // + displacement pass. The cache also disposes evicted entries.
    this._geomCache = new GeometryLRU(TERRAIN.geometryCacheSize);

    // ── Cascaded bump map ──────────────────────────────────────────────
    // One bump texture, sampled at three world-XZ tilings (near/mid/far),
    // blended in the fragment shader by 3D distance from the aircraft.
    // All tile materials share these uniforms so a single per-frame update
    // (in `update()`) propagates the aircraft position to every shader.
    this._aircraftPos = new THREE.Vector3();
    this._cascadedBumpTexture = this._loadCascadedBump(anisotropy);
    const cb = TERRAIN.cascadedBump;
    this._cascadedBumpUniforms = {
      uAircraftPos:       { value: this._aircraftPos },
      uBumpNearTile:      { value: cb.nearTile },
      uBumpMidTile:       { value: cb.midTile },
      uBumpFarTile:       { value: cb.farTile },
      uBumpNearFadeStart: { value: cb.nearFadeStart },
      uBumpNearFadeEnd:   { value: cb.nearFadeEnd },
      uBumpFarFadeStart:  { value: cb.farFadeStart },
      uBumpFarFadeEnd:    { value: cb.farFadeEnd },
      uColorBumpStrength: { value: cb.colorBumpStrength },
      uBumpStrengthNearDist: { value: cb.strengthNearDist },
      uBumpStrengthMidDist:  { value: cb.strengthMidDist },
      uBumpStrengthFarDist:  { value: cb.strengthFarDist },
      uBumpStrengthNear:     { value: cb.strengthNear },
      uBumpStrengthMid:      { value: cb.strengthMid },
      uBumpStrengthFar:      { value: cb.strengthFar },
      uBumpGrazingExp:       { value: cb.grazingExp },
      uBumpGrazingNearDist:  { value: cb.grazingNearDist },
      uBumpGrazingFarDist:   { value: cb.grazingFarDist },
      uBumpSampleOffsetCap:  { value: cb.sampleOffsetCap * cb.nearTile },
    };

    // ── Terrain grading ─────────────────────────────────────────────────
    // Terrain-only brightness / tint, shared by every terrain material and
    // re-pointed per lighting condition by `setGrading()`. See the comment
    // on TERRAIN.grading in SimConfig for why the ground needs its own set
    // of exposure knobs separate from the scene-wide ones.
    this._gradeUniforms = {
      uTerrainBrightness:   { value: 1 },
      uTerrainSunBoost:     { value: 1 },
      uTerrainAmbientBoost: { value: 1 },
      uTerrainTint:         { value: new THREE.Color(1, 1, 1) },
      uTerrainTintStrength: { value: 0 },
      uTerrainSaturation:   { value: 1 },
    };
    this.setGrading(TERRAIN.grading);

    // ── Shared geometries (one per LOD ring) ────────────────────────────
    // A PlaneGeometry is created in the XY plane then rotated into XZ so
    // +X is east, +Z is south (matches world convention used elsewhere).
    this._lodGeometries = TERRAIN.lodRings.map((ring) => {
      const g = new THREE.PlaneGeometry(
        TERRAIN.tileSize,
        TERRAIN.tileSize,
        ring.subdivisions,
        ring.subdivisions,
      );
      g.rotateX(-Math.PI / 2);
      return g;
    });

    // ── Materials ───────────────────────────────────────────────────────
    this._wireframeMats = TERRAIN.lodRings.map((_, i) => new THREE.MeshBasicMaterial({
      wireframe: true,
      color: TERRAIN.debugColorByLod
        ? LOD_DEBUG_COLORS[i % LOD_DEBUG_COLORS.length]
        : 0xffffff,
      fog: false,
      depthWrite: false,
    }));

    // Placeholder (non-debug) material — flat shaded until checkpoint 3
    // swaps in the streamed colour map.
    this._placeholderMat = new THREE.MeshStandardMaterial({
      color: 0x556b4a,
      roughness: 1,
      metalness: 0,
    });
    this._placeholderSkirtMat = this._makeSkirtMaterial(this._placeholderMat);
    this._applyTerrainPatch(this._placeholderMat);
    this._applyTerrainPatch(this._placeholderSkirtMat);

    // ── Mega tile ───────────────────────────────────────────────────────
    // One huge flat plane beneath the LOD tiles that extends far past the
    // playable grid, so there's always ground-coloured geometry under the
    // horizon even when LOD tiles are culled.
    this._megaTile = this._buildMegaTile(anisotropy);
    this._megaTile.visible = !this.debug;
    this.group.add(this._megaTile);

    // ── Build tile descriptors and spatial index ────────────────────────
    this._tiles = this._buildTiles();
    this._quadtree = this._buildQuadtree(this._tiles);

    // tileId → active mesh (only exists while the tile is visible)
    this._activeMeshes = new Map();

    // Reusable scratch set of visible tiles, reset each frame
    this._visibleTiles = new Set();
  }

  // ── Construction helpers ─────────────────────────────────────────────

  /**
   * Load the shared cascaded-bump texture. Wrap mode is RepeatWrapping (we
   * tile world-XZ coords directly into the sampler) and the texture is in
   * linear space (it's a height/bump source, not colour). Mipmaps matter:
   * the far cascade samples one tile per 1000 m, so without trilinear
   * filtering it would alias hideously.
   */
  _loadCascadedBump(anisotropy) {
    const loader = new THREE.TextureLoader();
    const tex = loader.load(`${import.meta.env.BASE_URL}assets/terrain/bump.jpg`);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.colorSpace = THREE.NoColorSpace;
    tex.anisotropy = anisotropy;
    tex.generateMipmaps = true;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    return tex;
  }

  /**
   * Build the mega tile: a 144480 × 144480 m flat plane (2× the terrain's
   * east-west extent) that sits at Y=-1 beneath the LOD tiles. Uses a single
   * colour map and no displacement — it's a backdrop, not playable terrain.
   */
  _buildMegaTile(anisotropy) {
    const size = 2 * TERRAIN.cols * TERRAIN.tileSize;
    const [ox, oz] = TERRAIN.origin;

    const geom = new THREE.PlaneGeometry(size, size, 1, 1);
    geom.rotateX(-Math.PI / 2);

    const loader = new THREE.TextureLoader();
    const map = loader.load(`${import.meta.env.BASE_URL}assets/terrain/mega-tile.jpg`);
    map.colorSpace = THREE.SRGBColorSpace;
    map.anisotropy = anisotropy;
    map.wrapS = THREE.ClampToEdgeWrapping;
    map.wrapT = THREE.ClampToEdgeWrapping;

    const mat = new THREE.MeshStandardMaterial({
      map,
      roughness: 1,
      metalness: 0,
    });
    // Grading + cloud shadows reach the horizon backdrop too, so the far
    // ground stays continuous with the LOD tiles in front of it.
    this._applyTerrainPatch(mat);

    const mesh = new THREE.Mesh(geom, mat);
    mesh.position.set(ox, 0, oz);
    mesh.receiveShadow = false;
    mesh.castShadow = false;
    mesh.frustumCulled = false;
    mesh.name = 'Terrain_MegaTile';
    return mesh;
  }

  _buildTiles() {
    const { cols, rows, tileSize, origin } = TERRAIN;
    const [ox, oz] = origin;
    const tiles = [];
    for (let r = 0; r < rows; r++) {
      const rowLetter = String.fromCharCode('A'.charCodeAt(0) + r);
      for (let c = 0; c < cols; c++) {
        const centerX = ox + (c - (cols - 1) / 2) * tileSize;
        const centerZ = oz + (r - (rows - 1) / 2) * tileSize;
        tiles.push({
          id: `${rowLetter}${c + 1}`,
          row: r,
          col: c,
          centerX,
          centerZ,
          minX: centerX - tileSize / 2,
          maxX: centerX + tileSize / 2,
          minZ: centerZ - tileSize / 2,
          maxZ: centerZ + tileSize / 2,
        });
      }
    }
    return tiles;
  }

  /** Bottom-up quadtree over the grid for frustum / distance culling. */
  _buildQuadtree(tiles) {
    const { cols, rows, tileSize, origin } = TERRAIN;
    const [ox, oz] = origin;
    const gridMinX = ox - (cols * tileSize) / 2;
    const gridMaxX = ox + (cols * tileSize) / 2;
    const gridMinZ = oz - (rows * tileSize) / 2;
    const gridMaxZ = oz + (rows * tileSize) / 2;

    const build = (subset, pMinX, pMinZ, pMaxX, pMaxZ) => {
      if (subset.length === 0) return null;
      // Bounds will be tightened from actual contents after structure is decided.
      const node = { minX: 0, minZ: 0, maxX: 0, maxZ: 0, tiles: null, children: null };

      if (subset.length === 1) {
        node.tiles = subset;
      } else {
        const midX = (pMinX + pMaxX) / 2;
        const midZ = (pMinZ + pMaxZ) / 2;
        const quads = [[], [], [], []];
        for (const t of subset) {
          const qx = t.centerX < midX ? 0 : 1;
          const qz = t.centerZ < midZ ? 0 : 1;
          quads[qz * 2 + qx].push(t);
        }

        // Safeguard: if subdivision didn't actually split the set, store as leaf.
        const splits = quads.filter((q) => q.length > 0).length;
        if (splits < 2) {
          node.tiles = subset;
        } else {
          node.children = [
            build(quads[0], pMinX, pMinZ, midX, midZ),
            build(quads[1], midX, pMinZ, pMaxX, midZ),
            build(quads[2], pMinX, midZ, midX, pMaxZ),
            build(quads[3], midX, midZ, pMaxX, pMaxZ),
          ];
        }
      }

      // Tighten this node's AABB to the union of its contents. The partition
      // rect (pMinX..pMaxX) is NOT a valid bound: tiles are assigned to a
      // quadrant by CENTER, so a tile whose center sits on a partition midline
      // (possible whenever a subdivision level has an odd number of tiles
      // along an axis — e.g., 12 cols → 6 → 3) has its AABB straddling that
      // midline. Storing the partition rect would produce a node AABB that's
      // tighter than the tiles it contains, and frustum-testing that tight
      // rect would reject the node just as the tile's center crosses a
      // frustum plane — even though half the tile is still visible.
      node.minX = Infinity; node.minZ = Infinity;
      node.maxX = -Infinity; node.maxZ = -Infinity;
      if (node.tiles) {
        for (const t of node.tiles) {
          if (t.minX < node.minX) node.minX = t.minX;
          if (t.minZ < node.minZ) node.minZ = t.minZ;
          if (t.maxX > node.maxX) node.maxX = t.maxX;
          if (t.maxZ > node.maxZ) node.maxZ = t.maxZ;
        }
      } else if (node.children) {
        for (const c of node.children) {
          if (!c) continue;
          if (c.minX < node.minX) node.minX = c.minX;
          if (c.minZ < node.minZ) node.minZ = c.minZ;
          if (c.maxX > node.maxX) node.maxX = c.maxX;
          if (c.maxZ > node.maxZ) node.maxZ = c.maxZ;
        }
      }
      return node;
    };

    return build(tiles, gridMinX, gridMinZ, gridMaxX, gridMaxZ);
  }

  // ── Public API ───────────────────────────────────────────────────────

  /** The shared height/colour texture cache (for TerrainCollider). */
  get cache() { return this._cache; }

  /**
   * Update visibility and LOD for every tile.
   * @param {THREE.Camera} camera — used for both position and frustum culling
   * @param {number} dt — seconds since last update (for fade-in animation)
   * @param {THREE.Vector3|null} aircraftPos — world-space aircraft position;
   *   feeds the cascaded-bump shader so distance bands are measured from the
   *   aircraft, not the (possibly offset) chase camera
   */
  update(camera, dt = 0, aircraftPos = null) {
    if (!camera) return;

    // Cascaded-bump shader reads this every frame via shared uniform.
    // Fall back to the camera position so the bump still cascades during
    // pre-scenario startup when there's no aircraft yet.
    if (aircraftPos) this._aircraftPos.copy(aircraftPos);
    else this._aircraftPos.setFromMatrixPosition(camera.matrixWorld);

    // Force a world-matrix recompute: without `force`, three.js skips when
    // matrixWorldNeedsUpdate is false, leaving matrixWorldInverse stale and
    // our frustum lagging one frame behind the camera.
    camera.updateMatrixWorld(true);
    _projScreenMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    _frustum.setFromProjectionMatrix(_projScreenMatrix);
    _camPos.setFromMatrixPosition(camera.matrixWorld);

    // Distance-cull radius: scale viewDistance by the frustum corner factor
    // so the horizontal XZ cull in _collectVisible doesn't clip tiles at the
    // far edges of the screen (where the frustum spreads wider than `far`).
    // Falls back to viewDistance for non-perspective cameras.
    let cullRadius = TERRAIN.viewDistance;
    if (camera.isPerspectiveCamera) {
      const vHalf = Math.tan(THREE.MathUtils.degToRad(camera.fov) * 0.5);
      const hHalf = vHalf * camera.aspect;
      const cornerFactor = Math.sqrt(1 + hHalf * hHalf + vHalf * vHalf);
      cullRadius = TERRAIN.viewDistance * cornerFactor;
    }

    const visible = this._visibleTiles;
    visible.clear();
    this._collectVisible(this._quadtree, _camPos, _frustum, cullRadius, visible);

    // Spawn / advance visible tiles
    for (const tile of visible) {
      const desiredLod = this._pickLod(tile, _camPos);
      let mesh = this._activeMeshes.get(tile.id);

      if (!mesh) {
        mesh = this._buildMesh(tile, desiredLod);
        this._activeMeshes.set(tile.id, mesh);
        this.group.add(mesh);
      }

      // Geometry and colour advance independently toward the desired LOD.
      // Each is a no-op until the corresponding stream is ready, so the
      // previous frame's content stays visible — no flat-fallback flicker.
      this._tryAdvanceGeometry(tile, mesh, desiredLod);
      this._tryAdvanceColor(tile, mesh, desiredLod);
      this._applyMaterial(mesh);
      this._maybeShow(mesh);
      this._advanceFade(mesh, dt);
    }

    // Retire tiles that fell out of view
    for (const [id, mesh] of this._activeMeshes) {
      if (!this._isVisible(id, visible)) {
        this._disposeMesh(mesh);
        this.group.remove(mesh);
        this._activeMeshes.delete(id);
      }
    }
  }

  /** Enable/disable wireframe debug mode. */
  setDebug(enabled) {
    if (this.debug === enabled) return;
    this.debug = enabled;
    if (this._megaTile) this._megaTile.visible = !enabled;
    for (const mesh of this._activeMeshes.values()) {
      this._applyMaterial(mesh);
      // Toggling debug ON may relax the visibility gate (geom-only is enough),
      // so re-evaluate. Toggling OFF can never hide a tile (visibility is
      // monotonic — _maybeShow short-circuits when already visible).
      this._maybeShow(mesh);
    }
  }

  toggleDebug() {
    this.setDebug(!this.debug);
  }

  dispose() {
    for (const mesh of this._activeMeshes.values()) {
      this._disposeMesh(mesh);
      this.group.remove(mesh);
    }
    this._activeMeshes.clear();
    for (const g of this._lodGeometries) g.dispose();
    this._lodGeometries.length = 0;
    for (const m of this._wireframeMats) m.dispose();
    this._wireframeMats.length = 0;
    this._placeholderMat.dispose();
    if (this._megaTile) {
      this.group.remove(this._megaTile);
      this._megaTile.geometry.dispose();
      if (this._megaTile.material.map) this._megaTile.material.map.dispose();
      this._megaTile.material.dispose();
      this._megaTile = null;
    }
    if (this._cascadedBumpTexture) {
      this._cascadedBumpTexture.dispose();
      this._cascadedBumpTexture = null;
    }
    this._cache.dispose();
    this._geomCache.dispose();
  }

  // ── Internal ─────────────────────────────────────────────────────────

  _collectVisible(node, camPos, frustum, cullRadius, out) {
    if (!node) return;

    // Distance cull: closest point on the node's XZ AABB to the camera.
    // `cullRadius` is viewDistance scaled by the frustum corner factor so
    // tiles visible at the screen edges aren't rejected before the frustum
    // test gets a chance to include them.
    const dx = Math.max(node.minX - camPos.x, 0, camPos.x - node.maxX);
    const dz = Math.max(node.minZ - camPos.z, 0, camPos.z - node.maxZ);
    if (Math.hypot(dx, dz) > cullRadius) return;

    // Frustum cull: generous Y range so tiles far above/below the camera
    // aren't culled before their actual relief would become visible.
    const halfH = Math.max(TERRAIN.heightScale * 2, 2000);
    _box.min.set(node.minX, -halfH, node.minZ);
    _box.max.set(node.maxX,  halfH, node.maxZ);
    if (!frustum.intersectsBox(_box)) return;

    if (node.tiles) {
      for (const t of node.tiles) {
        // Per-tile distance check using the tile's own AABB
        const tdx = Math.max(t.minX - camPos.x, 0, camPos.x - t.maxX);
        const tdz = Math.max(t.minZ - camPos.z, 0, camPos.z - t.maxZ);
        if (Math.hypot(tdx, tdz) <= cullRadius) {
          out.add(t);
        }
      }
    }
    if (node.children) {
      for (const c of node.children) this._collectVisible(c, camPos, frustum, cullRadius, out);
    }
  }

  _pickLod(tile, camPos) {
    const dx = camPos.x - tile.centerX;
    const dz = camPos.z - tile.centerZ;
    const dist = Math.hypot(dx, dz);
    const rings = TERRAIN.lodRings;
    for (let i = 0; i < rings.length; i++) {
      if (dist <= rings[i].maxDist) return i;
    }
    return rings.length - 1;
  }

  _buildMesh(tile, desiredLod) {
    // Initial state: shared flat geometry at the desired LOD, placeholder
    // material, hidden. The mesh stays invisible until _maybeShow flips it
    // on, which happens once the streams it needs are ready (geom alone in
    // debug mode, geom + colour in normal mode).
    const mesh = new THREE.Mesh(this._lodGeometries[desiredLod], this._placeholderMat);
    mesh.position.set(tile.centerX, 0, tile.centerZ);
    mesh.receiveShadow = true;
    mesh.castShadow = false;
    // We already cull with the quadtree; skip three.js built-in frustum check.
    mesh.frustumCulled = false;
    mesh.visible = false;
    mesh.userData.tileId = tile.id;
    mesh.userData.geomLod = desiredLod;     // LOD of currently rendered geometry
    mesh.userData.geomRes = null;           // textureRes of currently displaced geometry
    mesh.userData.colorLod = null;          // LOD of currently applied colour
    mesh.userData.colorRes = null;          // textureRes of currently applied colour
    mesh.userData.geomKey = null;           // `${tileId}/${lod}/${res}` when displaced
    mesh.userData.colorKey = null;          // `${tileId}/${lod}/${res}` when colour applied
    mesh.userData.ownedGeometry = false;    // true if mesh.geometry is a per-tile clone
    mesh.userData.tileMaterial = null;      // per-tile MeshStandardMaterial (lazy)
    mesh.userData.skirtMaterial = null;    // per-tile skirt variant with polygonOffset
    mesh.userData.fadeStarted = false;      // true once opacity ramp has begun
    mesh.userData.fadeT = 0;                // seconds elapsed since fade start
    mesh.name = `Terrain_${tile.id}`;
    return mesh;
  }

  /**
   * Build a new displaced geometry for the desired LOD if its heightmap is
   * available. The previous geometry stays attached to the mesh until the
   * replacement is ready, so LOD transitions don't flatten the tile.
   *
   * Also consults the displaced-geometry LRU first: if this exact
   * (tile, LOD, res) was recently visible, reuse its prior geometry.
   */
  _tryAdvanceGeometry(tile, mesh, desiredLod) {
    const ring = TERRAIN.lodRings[desiredLod];
    // Settled-tile fast path: bail out before allocating the key string, which
    // otherwise happens every frame for every visible tile. geomKey encodes
    // tile.id/lod/res and tile.id is invariant per mesh, so matching (lod, res)
    // numerically is equivalent to matching the full key.
    if (mesh.userData.geomKey !== null
      && mesh.userData.geomLod === desiredLod
      && mesh.userData.geomRes === ring.textureRes) return;

    const desiredKey = `${tile.id}/${desiredLod}/${ring.textureRes}`;

    // Cache hit: a previous mesh built and discarded this exact geometry.
    let geom = this._geomCache.take(desiredKey);

    if (!geom) {
      const imageData = this._cache.getHeight(tile.id, ring.textureRes);
      if (!imageData) return;  // not ready — keep showing whatever's currently there
      geom = this._buildDisplacedGeometry(desiredLod, imageData);
    }

    // Atomic swap. Hand the outgoing geometry to the cache rather than
    // dispose it — it may be needed again soon as the camera moves.
    if (mesh.userData.ownedGeometry && mesh.geometry && mesh.userData.geomKey) {
      this._geomCache.put(mesh.userData.geomKey, mesh.geometry);
    }
    mesh.geometry = geom;
    mesh.userData.geomLod = desiredLod;
    mesh.userData.geomRes = ring.textureRes;
    mesh.userData.geomKey = desiredKey;
    mesh.userData.ownedGeometry = true;
  }

  /**
   * Swap the tile's per-tile material map to the desired-LOD colour texture
   * if it's available. Independent of geometry advancement — a tile may
   * briefly display LOD 0 geometry with LOD 1 colour while textures stream.
   */
  _tryAdvanceColor(tile, mesh, desiredLod) {
    const ring = TERRAIN.lodRings[desiredLod];
    // Settled-tile fast path — see _tryAdvanceGeometry. Avoids the per-frame
    // key-string allocation once the colour is at the desired LOD/res.
    if (mesh.userData.colorKey !== null
      && mesh.userData.colorLod === desiredLod
      && mesh.userData.colorRes === ring.textureRes) return;

    const desiredKey = `${tile.id}/${desiredLod}/${ring.textureRes}`;

    const tex = this._cache.getColor(tile.id, ring.textureRes);
    if (!tex) return;

    let mat = mesh.userData.tileMaterial;
    const firstTime = !mat;
    if (!mat) {
      mat = new THREE.MeshStandardMaterial({
        map: tex,
        // Cascaded bump: same texture across every tile, sampled at three
        // world-XZ tilings inside the shader (see _setupCascadedBumpShader).
        bumpMap: this._cascadedBumpTexture,
        bumpScale: TERRAIN.cascadedBump.scale,
        roughness: 1,
        metalness: 0,
      });
      this._setupCascadedBumpShader(mat);
      mesh.userData.tileMaterial = mat;
      const skirtMat = this._makeSkirtMaterial(mat);
      this._setupCascadedBumpShader(skirtMat);
      mesh.userData.skirtMaterial = skirtMat;
    } else if (mat.map !== tex) {
      mat.map = tex;
      mat.needsUpdate = true;
      if (mesh.userData.skirtMaterial) {
        mesh.userData.skirtMaterial.map = tex;
        mesh.userData.skirtMaterial.needsUpdate = true;
      }
    }
    mesh.userData.colorLod = desiredLod;
    mesh.userData.colorRes = ring.textureRes;
    mesh.userData.colorKey = desiredKey;

    // If this was the first colour applied to an already-visible mesh
    // (e.g. tile became visible in debug mode without a tileMaterial),
    // start a fade so the colour ease-in isn't a hard pop.
    if (firstTime) this._startFadeIfNeeded(mesh);
  }

  /**
   * Wire a per-tile MeshStandardMaterial up with the cascaded-bump shader
   * via onBeforeCompile. The bump texture is sampled at three world-XZ
   * tilings (near/mid/far), and the three samples are blended in the
   * fragment shader by the fragment's 3D distance to the aircraft. Uniform
   * objects come from `_cascadedBumpUniforms`, so all tile materials share
   * a single Vector3 for the aircraft position — one update per frame in
   * `update()` propagates to every shader.
   *
   * Three.js's standard bump pipeline computes screen-space derivatives of
   * the height via dFdx/dFdy and feeds them to perturbNormalArb. We hijack
   * `dHdxy_fwd` to evaluate the height as `cascadedHeight(worldXZ)` instead
   * of the usual `texture(bumpMap, vBumpMapUv)` — the surrounding Mikkelsen
   * math is parameterization-agnostic so perturbNormalArb stays unchanged.
   */
  _setupCascadedBumpShader(material) {
    const shared = this._cascadedBumpUniforms;
    // Distinguish the bump(+grade,+cloud-shadow) program from any other standard
    // material sharing these defines — onBeforeCompile isn't in the default key.
    const key = this._cloudShadowUniforms ? 'terrainBumpGradeCloudShadow' : 'terrainBumpGrade';
    material.customProgramCacheKey = () => `${key}|${this._cloudShadowSteps}`;
    material.onBeforeCompile = (shader) => {
      // Share uniform refs by assigning the same { value } objects. WebGL
      // reads through the reference each frame, so updating shared.* in
      // one place updates every material.
      shader.uniforms.uAircraftPos       = shared.uAircraftPos;
      shader.uniforms.uBumpNearTile      = shared.uBumpNearTile;
      shader.uniforms.uBumpMidTile       = shared.uBumpMidTile;
      shader.uniforms.uBumpFarTile       = shared.uBumpFarTile;
      shader.uniforms.uBumpNearFadeStart = shared.uBumpNearFadeStart;
      shader.uniforms.uBumpNearFadeEnd   = shared.uBumpNearFadeEnd;
      shader.uniforms.uBumpFarFadeStart  = shared.uBumpFarFadeStart;
      shader.uniforms.uBumpFarFadeEnd    = shared.uBumpFarFadeEnd;
      shader.uniforms.uColorBumpStrength = shared.uColorBumpStrength;
      shader.uniforms.uBumpStrengthNearDist = shared.uBumpStrengthNearDist;
      shader.uniforms.uBumpStrengthMidDist  = shared.uBumpStrengthMidDist;
      shader.uniforms.uBumpStrengthFarDist  = shared.uBumpStrengthFarDist;
      shader.uniforms.uBumpStrengthNear     = shared.uBumpStrengthNear;
      shader.uniforms.uBumpStrengthMid      = shared.uBumpStrengthMid;
      shader.uniforms.uBumpStrengthFar      = shared.uBumpStrengthFar;
      shader.uniforms.uBumpGrazingExp       = shared.uBumpGrazingExp;
      shader.uniforms.uBumpGrazingNearDist  = shared.uBumpGrazingNearDist;
      shader.uniforms.uBumpGrazingFarDist   = shared.uBumpGrazingFarDist;
      shader.uniforms.uBumpSampleOffsetCap  = shared.uBumpSampleOffsetCap;

      // ── Vertex: emit world-space position as a varying ─────────────
      shader.vertexShader = shader.vertexShader.replace(
        '#include <common>',
        `#include <common>
varying vec3 vTerrainWorldPos;`
      );
      shader.vertexShader = shader.vertexShader.replace(
        '#include <fog_vertex>',
        `#include <fog_vertex>
vTerrainWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;`
      );

      // ── Fragment: declare custom varying + uniforms ────────────────
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <common>',
        `#include <common>
varying vec3 vTerrainWorldPos;
uniform vec3 uAircraftPos;
uniform float uBumpNearTile;
uniform float uBumpMidTile;
uniform float uBumpFarTile;
uniform float uBumpNearFadeStart;
uniform float uBumpNearFadeEnd;
uniform float uBumpFarFadeStart;
uniform float uBumpFarFadeEnd;
uniform float uColorBumpStrength;
uniform float uBumpStrengthNearDist;
uniform float uBumpStrengthMidDist;
uniform float uBumpStrengthFarDist;
uniform float uBumpStrengthNear;
uniform float uBumpStrengthMid;
uniform float uBumpStrengthFar;
uniform float uBumpGrazingExp;
uniform float uBumpGrazingNearDist;
uniform float uBumpGrazingFarDist;
uniform float uBumpSampleOffsetCap;`
      );

      // ── Fragment: replace the bump_pars chunk wholesale ────────────
      // Same `dHdxy_fwd` / `perturbNormalArb` interface as three.js, but
      // the height is sampled from the bump texture three times in world
      // XZ space and blended by 3D distance to the aircraft.
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <bumpmap_pars_fragment>',
        `#ifdef USE_BUMPMAP
  uniform sampler2D bumpMap;
  uniform float bumpScale;

  // Sample bumpMap at three world-XZ tilings; smooth-blend by distance.
  // Each cascade tiles once every uBump*Tile metres → uv = worldXZ / tile.
  float terrainCascadedHeight(vec2 worldXZ, float dist) {
    float hN = texture2D(bumpMap, worldXZ / uBumpNearTile).x;
    float hM = texture2D(bumpMap, worldXZ / uBumpMidTile).x;
    float hF = texture2D(bumpMap, worldXZ / uBumpFarTile).x;
    float wNear = 1.0 - smoothstep(uBumpNearFadeStart, uBumpNearFadeEnd, dist);
    float wFar  = smoothstep(uBumpFarFadeStart, uBumpFarFadeEnd, dist);
    float wMid  = max(0.0, 1.0 - wNear - wFar);
    float total = wNear + wMid + wFar;
    return (hN * wNear + hM * wMid + hF * wFar) / max(total, 1e-5);
  }

  // Luminance from the colour map at the tile UV — adds terrain-scale
  // bump detail that follows the satellite imagery features.
  float colorMapHeight(vec2 uv) {
    vec3 c = texture2D(map, uv).rgb;
    return dot(c, vec3(0.2126, 0.7152, 0.0722));
  }

  // Distance-based bump-strength multiplier: piecewise-linear, peaks near
  // the camera, baseline at mid range, attenuated at far range.
  float terrainBumpStrengthAtDist(float dist) {
    float nearW = 1.0 - smoothstep(uBumpStrengthNearDist, uBumpStrengthMidDist, dist);
    float farW  = smoothstep(uBumpStrengthMidDist, uBumpStrengthFarDist, dist);
    // mix near→mid as dist crosses near→mid, then mid→far as dist crosses mid→far.
    return mix(mix(uBumpStrengthMid, uBumpStrengthNear, nearW), uBumpStrengthFar, farW);
  }

  vec2 dHdxy_fwd() {
    vec2 wxz = vTerrainWorldPos.xz;
    float dist = distance(vTerrainWorldPos, uAircraftPos);

    // Cascaded bump height
    float hBump = terrainCascadedHeight(wxz, dist);
    // Colour-map luminance height (tile UV from Three.js)
    float hColor = colorMapHeight(vMapUv);

    // Combined bump scale: base × distance multiplier × grazing-angle fade.
    // The grazing fade counters the dFdx/dFdy explosion + 1/fDet in
    // perturbNormalArb that amplifies bump on highly foreshortened pixels.
    // The artifact only appears at distance, so the fade is disabled within
    // uBumpGrazingNearDist and ramps in by uBumpGrazingFarDist — close
    // fragments keep full bump regardless of viewing angle.
    float distMul = terrainBumpStrengthAtDist(dist);
    float grazeMul = 1.0;
    if (uBumpGrazingExp > 0.0) {
      vec3 V = normalize(cameraPosition - vTerrainWorldPos);
      // Geometric normal from screen-space derivatives (matches perturbNormalArb's
      // basis), so the fade correlates with the same foreshortening that drives
      // the artifact. Sign-fix via faceforward against world up keeps it stable.
      vec3 dpx = dFdx(vTerrainWorldPos);
      vec3 dpy = dFdy(vTerrainWorldPos);
      vec3 Ng = normalize(cross(dpx, dpy));
      Ng = faceforward(Ng, -vec3(0.0, 1.0, 0.0), Ng);
      float graze = pow(clamp(dot(V, Ng), 0.0, 1.0), uBumpGrazingExp);
      float grazeBlend = smoothstep(uBumpGrazingNearDist, uBumpGrazingFarDist, dist);
      grazeMul = mix(1.0, graze, grazeBlend);
    }
    float bs = bumpScale * distMul * grazeMul;

    float Hll = bs * mix(hBump, hBump + hColor, uColorBumpStrength);

    // Cap world-space sample offsets: at glancing angles dFdx(wxz) can span
    // hundreds of metres, sampling unrelated parts of the bump map.
    vec2 dx = dFdx(wxz);
    vec2 dy = dFdy(wxz);
    float dxLen = length(dx);
    float dyLen = length(dy);
    if (dxLen > uBumpSampleOffsetCap) dx *= uBumpSampleOffsetCap / dxLen;
    if (dyLen > uBumpSampleOffsetCap) dy *= uBumpSampleOffsetCap / dyLen;
    vec2 dxUv = dFdx(vMapUv);
    vec2 dyUv = dFdy(vMapUv);

    float hBumpDx = terrainCascadedHeight(wxz + dx, dist);
    float hColorDx = colorMapHeight(vMapUv + dxUv);
    float dBx = bs * mix(hBumpDx, hBumpDx + hColorDx, uColorBumpStrength) - Hll;

    float hBumpDy = terrainCascadedHeight(wxz + dy, dist);
    float hColorDy = colorMapHeight(vMapUv + dyUv);
    float dBy = bs * mix(hBumpDy, hBumpDy + hColorDy, uColorBumpStrength) - Hll;

    return vec2(dBx, dBy);
  }

  // Unchanged from three.js bumpmap_pars_fragment — Mikkelsen TBN-less
  // perturbation, parameterization-agnostic w.r.t. dHdxy.
  vec3 perturbNormalArb( vec3 surf_pos, vec3 surf_norm, vec2 dHdxy, float faceDirection ) {
    vec3 vSigmaX = normalize( dFdx( surf_pos.xyz ) );
    vec3 vSigmaY = normalize( dFdy( surf_pos.xyz ) );
    vec3 vN = surf_norm;
    vec3 R1 = cross( vSigmaY, vN );
    vec3 R2 = cross( vN, vSigmaX );
    float fDet = dot( vSigmaX, R1 ) * faceDirection;
    vec3 vGrad = sign( fDet ) * ( dHdxy.x * R1 + dHdxy.y * R2 );
    return normalize( abs( fDet ) * surf_norm - vGrad );
  }
#endif`
      );

      // Compose the grading and cloud-shadow injections into the same compile
      // (a material has only one onBeforeCompile). Their anchors are disjoint
      // from the bump patch's, and the cloud-shadow one is a no-op when
      // shadows are disabled.
      this._patchGradeShader(shader);
      this._patchCloudShadowShader(shader);
    };
  }

  /**
   * Apply the terrain-wide shader patches (grading, plus cloud shadows when
   * enabled) to a material that has no other onBeforeCompile of its own — the
   * mega tile and the placeholder. Per-tile materials fold the same patches
   * into their cascaded-bump compile instead.
   */
  _applyTerrainPatch(material) {
    material.onBeforeCompile = (shader) => {
      this._patchGradeShader(shader);
      this._patchCloudShadowShader(shader);
    };
    // onBeforeCompile isn't part of three's default program cache key, so mark
    // patched materials to keep them from sharing a compiled program with an
    // unpatched material that happens to have the same defines.
    const key = this._cloudShadowUniforms ? 'terrainGradeCloudShadow' : 'terrainGrade';
    material.customProgramCacheKey = () => `${key}|${this._cloudShadowSteps}`;
  }

  /**
   * Inject the terrain grading into a shader being compiled.
   *
   * Two separate stages, because "the ground is too dark" and "the ground is
   * the wrong colour" want different fixes:
   *
   *   1. Albedo (right after `<map_fragment>`) — desaturate the baked
   *      satellite colour, then blend toward `albedo × tint`. This recolours
   *      the ground itself, so both the sunlit and the sky-lit parts shift
   *      hue together and the terrain can be pulled toward the condition's
   *      palette instead of staying stuck at daylight-satellite green.
   *
   *   2. Reflected light (at `<lights_fragment_end>`) — scale the direct
   *      (sun) and indirect (HDR environment + AmbientLight) diffuse terms
   *      independently, then apply the overall brightness. Splitting the two
   *      is the point: the terrain's normal faces straight up, so it drinks
   *      the whole sky hemisphere while a low sun only ever lands `N·L` of
   *      its intensity on it. Raising `sunBoost` (and/or lowering
   *      `ambientBoost`) is what makes `sun.intensity` actually shape the
   *      ground rather than the aircraft alone.
   *
   * Grading multiplies light rather than the final pixel, so scene fog still
   * blends the graded terrain toward the horizon exactly as before.
   */
  _patchGradeShader(shader) {
    Object.assign(shader.uniforms, this._gradeUniforms);

    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <common>',
      `#include <common>
uniform float uTerrainBrightness;
uniform float uTerrainSunBoost;
uniform float uTerrainAmbientBoost;
uniform vec3  uTerrainTint;
uniform float uTerrainTintStrength;
uniform float uTerrainSaturation;`
    );

    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <map_fragment>',
      `#include <map_fragment>
{
  // Saturation first, so pulling the satellite imagery toward grey lets the
  // tint dominate rather than fighting the baked-in hue.
  float terrainLum = dot(diffuseColor.rgb, vec3(0.2126, 0.7152, 0.0722));
  diffuseColor.rgb = mix(vec3(terrainLum), diffuseColor.rgb, uTerrainSaturation);
  diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * uTerrainTint, uTerrainTintStrength);
}`
    );

    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <lights_fragment_end>',
      `#include <lights_fragment_end>
reflectedLight.directDiffuse    *= uTerrainSunBoost * uTerrainBrightness;
reflectedLight.indirectDiffuse  *= uTerrainAmbientBoost * uTerrainBrightness;
reflectedLight.directSpecular   *= uTerrainBrightness;
reflectedLight.indirectSpecular *= uTerrainBrightness;`
    );
  }

  /**
   * Point the shared grading uniforms at a lighting condition's `terrain`
   * block. Missing fields fall back to the TERRAIN.grading defaults, so a
   * condition with no `terrain: {}` block renders exactly as it did before
   * grading existed. Called by App.applyLighting on every scenario change and
   * on every debug-slider edit — it only writes uniform values, so nothing
   * recompiles.
   */
  setGrading(cfg) {
    const c = cfg || {};
    const d = TERRAIN.grading;
    const u = this._gradeUniforms;
    u.uTerrainBrightness.value   = c.brightness   ?? d.brightness;
    u.uTerrainSunBoost.value     = c.sunBoost     ?? d.sunBoost;
    u.uTerrainAmbientBoost.value = c.ambientBoost ?? d.ambientBoost;
    u.uTerrainTintStrength.value = c.tintStrength ?? d.tintStrength;
    u.uTerrainSaturation.value   = c.saturation   ?? d.saturation;
    // setHex defaults to sRGB input and converts into the renderer's working
    // (linear) space — matching the decoded colour map it multiplies.
    u.uTerrainTint.value.setHex(c.tint ?? d.tint);
  }

  /**
   * Inject the cloud-shadow sampling into a shader being compiled: share the
   * uniform block, carry the world-space fragment position through as a
   * varying, and multiply the lit surface by cloudShadowFactor() just before
   * the fragment is written. Anchors (project_vertex / opaque_fragment, plus
   * prepends) are disjoint from the cascaded-bump patch so the two compose.
   */
  _buildCloudShadowDefines() {
    if (!this._cloudShadowUniforms) return '';
    return `#define CLOUD_SHADOW_OCTAVES ${Math.max(1, CLOUDS.shadow.octaves | 0)}\n`
      + `#define CLOUD_SHADOW_STEPS ${this._cloudShadowSteps}\n`;
  }

  /**
   * Retune the shadow's sun-ray sample count — the terrain half of the cloud
   * quality preset. It's a shader #define, and unlike a ShaderMaterial's
   * `defines` it lives in source we inject ourselves, so three can't see it: the
   * step count is part of `customProgramCacheKey` (so the old program isn't
   * handed straight back) and every live terrain material has to be marked for
   * recompile. Materials built after this point read the new defines string on
   * their first compile. Costs one hitch as the programs relink.
   */
  setCloudShadowSteps(steps) {
    if (!this._cloudShadowUniforms) return;
    const s = Math.max(1, steps | 0);
    if (s === this._cloudShadowSteps) return;
    this._cloudShadowSteps = s;
    this._cloudShadowDefines = this._buildCloudShadowDefines();
    this._forEachTerrainMaterial((m) => { m.needsUpdate = true; });
  }

  /**
   * Visit every terrain material currently alive: the placeholder pair, the mega
   * tile, and the lazily-created colour + skirt material of each visible tile.
   * Tiles that aren't streamed in yet have no material to visit — they'll pick up
   * the current shader state when they build one.
   */
  _forEachTerrainMaterial(fn) {
    fn(this._placeholderMat);
    fn(this._placeholderSkirtMat);
    fn(this._megaTile.material);
    for (const mesh of this._activeMeshes.values()) {
      if (mesh.userData.tileMaterial) fn(mesh.userData.tileMaterial);
      if (mesh.userData.skirtMaterial) fn(mesh.userData.skirtMaterial);
    }
  }

  _patchCloudShadowShader(shader) {
    if (!this._cloudShadowUniforms) return;
    Object.assign(shader.uniforms, this._cloudShadowUniforms);

    // Vertex: emit world-space position for the fragment's sun-ray march.
    shader.vertexShader = `varying vec3 vCloudShadowWorldPos;\n${shader.vertexShader}`;
    shader.vertexShader = shader.vertexShader.replace(
      '#include <project_vertex>',
      `#include <project_vertex>
      vCloudShadowWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;`
    );

    // Fragment: prepend the shadow density/sampling functions, then attenuate
    // the outgoing radiance by the sun-ray transmittance through the deck.
    shader.fragmentShader = `${this._cloudShadowDefines}${cloudShadowPars}\n${shader.fragmentShader}`;
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <opaque_fragment>',
      `outgoingLight *= cloudShadowFactor(vCloudShadowWorldPos);
      #include <opaque_fragment>`
    );
  }

  /**
   * Clone a material with polygonOffset enabled so skirt fragments are
   * pushed slightly behind the surface in depth-buffer space.
   */
  _makeSkirtMaterial(baseMat) {
    const mat = baseMat.clone();
    mat.polygonOffset = true;
    mat.polygonOffsetFactor = 1;
    mat.polygonOffsetUnits = 1;
    return mat;
  }

  /**
   * Pick the right material for the mesh's current state. Cheap to call
   * every frame — if it's already correct, the assignment is a no-op.
   *
   * Displaced geometries have two groups (surface + skirt), so the mesh
   * receives a two-element material array. The skirt variant uses
   * polygonOffset to lose depth fights against the coplanar surface edge.
   */
  _applyMaterial(mesh) {
    if (this.debug) {
      const wf = this._wireframeMats[mesh.userData.geomLod];
      mesh.material = mesh.geometry.groups.length ? [wf, wf] : wf;
    } else {
      const surf = mesh.userData.tileMaterial || this._placeholderMat;
      const skirt = mesh.userData.skirtMaterial || this._placeholderSkirtMat;
      mesh.material = mesh.geometry.groups.length ? [surf, skirt] : surf;
    }
  }

  /**
   * Reveal the mesh once the streams it needs are ready. Visibility is
   * monotonic — once shown, never hidden — so toggling debug mode never
   * makes a tile blink out.
   */
  _maybeShow(mesh) {
    if (mesh.visible) return;
    const hasGeom  = mesh.userData.geomKey  !== null;
    const hasColor = mesh.userData.colorKey !== null;
    // Debug needs only the geometry; the wireframe materials are shared.
    // Non-debug additionally waits for the per-tile colour material so the
    // first thing the user sees is fully-textured terrain.
    const ready = hasGeom && (this.debug || hasColor);
    if (!ready) return;
    mesh.visible = true;
    this._startFadeIfNeeded(mesh);
  }

  /**
   * Begin (or restart) the opacity ramp on a tile's colour material.
   * No-op if the mesh has no per-tile material (debug mode), is already
   * fading, or isn't yet visible.
   */
  _startFadeIfNeeded(mesh) {
    if (mesh.userData.fadeStarted) return;
    if (!mesh.visible) return;
    const mat = mesh.userData.tileMaterial;
    if (!mat) return;

    mesh.userData.fadeStarted = true;
    mesh.userData.fadeT = 0;
    mat.transparent = true;
    mat.opacity = 0;
    const skirtMat = mesh.userData.skirtMaterial;
    if (skirtMat) { skirtMat.transparent = true; skirtMat.opacity = 0; }
  }

  /** Advance the per-mesh fade timer and update material opacity. */
  _advanceFade(mesh, dt) {
    if (!mesh.userData.fadeStarted) return;
    if (mesh.userData.fadeT >= TERRAIN.fadeInDuration) return;

    mesh.userData.fadeT += dt;
    const t = Math.min(mesh.userData.fadeT / TERRAIN.fadeInDuration, 1);
    const mat = mesh.userData.tileMaterial;
    if (!mat) return;

    mat.opacity = t;
    const skirtMat = mesh.userData.skirtMaterial;
    if (skirtMat) skirtMat.opacity = t;
    if (t >= 1) {
      mat.transparent = false;
      mat.opacity = 1;
      if (skirtMat) { skirtMat.transparent = false; skirtMat.opacity = 1; }
    }
  }

  /**
   * Build a fresh per-tile displaced geometry: clone the shared LOD plane,
   * write heightmap-sampled Y values into every vertex, then append a
   * perimeter skirt strip to hide LOD-boundary cracks.
   */
  _buildDisplacedGeometry(lodIndex, imageData) {
    const geom = this._lodGeometries[lodIndex].clone();
    this._displaceVertices(geom, imageData);
    if (TERRAIN.skirtDepth > 0) this._addSkirts(geom);
    // Recompute normals after structural changes — delete the old attribute
    // first so the new one is sized for the augmented vertex count.
    geom.deleteAttribute('normal');
    geom.computeVertexNormals();
    geom.computeBoundingBox();
    geom.computeBoundingSphere();
    return geom;
  }

  /**
   * Sample a heightmap for each vertex of `geom` (assumed XZ-aligned,
   * centred at origin) and write luminance-scaled elevation into vertex Y.
   *
   * UV convention:
   *   - local +X (east)  → image column W-1
   *   - local -Z (north) → image row 0     (A1 is north-west)
   *   - local +Z (south) → image row H-1
   */
  _displaceVertices(geom, imageData) {
    const pos = geom.attributes.position;
    const arr = pos.array;
    const { width, height, data } = imageData;
    const half = TERRAIN.tileSize / 2;
    const scale = TERRAIN.heightScale;
    const bias = TERRAIN.heightBias;

    for (let i = 0; i < pos.count; i++) {
      const x = arr[i * 3 + 0];
      const z = arr[i * 3 + 2];

      // Local (x, z) → normalized (u, v) in [0, 1]
      //   u: 0 at west edge, 1 at east edge
      //   v: 0 at south edge, 1 at north edge
      const u = (x + half) / TERRAIN.tileSize;
      const v = (half - z) / TERRAIN.tileSize;

      // Image row 0 is the north edge (A1 top-left → north-west).
      const px = Math.min(width - 1,  Math.max(0, Math.round(u * (width - 1))));
      const py = Math.min(height - 1, Math.max(0, Math.round((1 - v) * (height - 1))));
      const idx = (py * width + px) * 4;

      // JPG is grayscale-ish — use the red channel as the height sample.
      const lum = data[idx] / 255;
      arr[i * 3 + 1] = bias + lum * scale;
    }

    pos.needsUpdate = true;
  }

  /**
   * Append a perimeter skirt strip to `geom`.
   *
   *   1. For each outer-ring vertex, create TWO new vertices: a duplicate
   *      at the same XYZ (skirt top) and another dropped by `skirtDepth`
   *      (skirt bottom). The surface's original perimeter vertices stay
   *      attached only to surface triangles, and the skirt's own top
   *      duplicates stay attached only to wall triangles. Without this
   *      split, `computeVertexNormals` would average upward surface normals
   *      with sideways wall normals at every edge vertex, producing a
   *      visible ~45° crease in the material shading.
   *
   *   2. Stitch each top/bottom pair with an outward-facing quad.
   *
   *  PlaneGeometry stores vertex Y as `-y`, so post-rotation iy=0 is the
   *  NORTH edge (z = -s/2) and iy=w-1 is the SOUTH edge. The walk below is
   *  therefore CW when viewed from +Y, which means "outside" is to the
   *  LEFT of travel — the winding (top, bot1, bot0)/(top, top1, bot1)
   *  gives front faces that point outward on every side.
   */
  _addSkirts(geom) {
    const skirtDepth = TERRAIN.skirtDepth;
    const posAttr = geom.attributes.position;
    const uvAttr  = geom.attributes.uv;
    const indexAttr = geom.index;
    if (!posAttr || !uvAttr || !indexAttr) return;

    const pos = posAttr.array;
    const uv  = uvAttr.array;
    const oldIdx = indexAttr.array;

    // Shared LOD geometries are square (sub+1)² grids.
    const totalVerts = posAttr.count;
    const w = Math.round(Math.sqrt(totalVerts));

    // Walk the outer ring CW-from-above: north → east → south → west.
    const perim = [];
    for (let ix = 0; ix < w; ix++)            perim.push(0 * w + ix);            // north (iy=0)
    for (let iy = 1; iy < w; iy++)            perim.push(iy * w + (w - 1));      // east  (ix=w-1)
    for (let ix = w - 2; ix >= 0; ix--)       perim.push((w - 1) * w + ix);      // south (iy=w-1)
    for (let iy = w - 2; iy >= 1; iy--)       perim.push(iy * w + 0);            // west  (ix=0)

    const perimLen = perim.length;
    const baseN = totalVerts;
    // Two new verts per perimeter slot: one top duplicate + one bottom.
    const newVertCount = baseN + 2 * perimLen;

    // ── New position / UV buffers ───────────────────────────────────────
    const newPos = new Float32Array(newVertCount * 3);
    const newUv  = new Float32Array(newVertCount * 2);
    newPos.set(pos);
    newUv.set(uv);

    for (let k = 0; k < perimLen; k++) {
      const srcVi = perim[k];
      const topVi = baseN + k;
      const botVi = baseN + perimLen + k;
      const sx = pos[srcVi * 3 + 0];
      const sy = pos[srcVi * 3 + 1];
      const sz = pos[srcVi * 3 + 2];
      const su = uv[srcVi * 2 + 0];
      const sv = uv[srcVi * 2 + 1];

      // Top duplicate — same XYZ/UV as the source surface vertex. Gets its
      // own normal (wall-sideways) rather than averaging with the surface.
      newPos[topVi * 3 + 0] = sx;
      newPos[topVi * 3 + 1] = sy;
      newPos[topVi * 3 + 2] = sz;
      newUv[topVi * 2 + 0]  = su;
      newUv[topVi * 2 + 1]  = sv;

      // Bottom — dropped by skirtDepth.
      newPos[botVi * 3 + 0] = sx;
      newPos[botVi * 3 + 1] = sy - skirtDepth;
      newPos[botVi * 3 + 2] = sz;
      newUv[botVi * 2 + 0]  = su;
      newUv[botVi * 2 + 1]  = sv;
    }

    // ── New index buffer ────────────────────────────────────────────────
    const oldIdxLen = oldIdx.length;
    const newIdxLen = oldIdxLen + perimLen * 6;
    const IdxArray = newVertCount > 65535 ? Uint32Array : Uint16Array;
    const newIdx = new IdxArray(newIdxLen);
    newIdx.set(oldIdx);

    let off = oldIdxLen;
    for (let k = 0; k < perimLen; k++) {
      const k1 = (k + 1) % perimLen;
      const top0 = baseN + k;
      const top1 = baseN + k1;
      const bot0 = baseN + perimLen + k;
      const bot1 = baseN + perimLen + k1;
      // Tri 1: (top0, bot1, bot0) — outward-facing
      newIdx[off++] = top0;
      newIdx[off++] = bot1;
      newIdx[off++] = bot0;
      // Tri 2: (top0, top1, bot1)
      newIdx[off++] = top0;
      newIdx[off++] = top1;
      newIdx[off++] = bot1;
    }

    geom.setAttribute('position', new THREE.BufferAttribute(newPos, 3));
    geom.setAttribute('uv',       new THREE.BufferAttribute(newUv, 2));
    geom.setIndex(new THREE.BufferAttribute(newIdx, 1));

    // Separate surface and skirt into material groups so the skirt can use
    // polygonOffset to avoid z-fighting with coplanar surface edges.
    geom.addGroup(0, oldIdxLen, 0);                    // group 0 = surface
    geom.addGroup(oldIdxLen, perimLen * 6, 1);         // group 1 = skirt
  }

  _disposeMesh(mesh) {
    // Hand owned geometry to the LRU instead of disposing — same tile may
    // re-enter view shortly and we'd otherwise rebuild it from scratch.
    if (mesh.userData.ownedGeometry && mesh.geometry && mesh.userData.geomKey) {
      this._geomCache.put(mesh.userData.geomKey, mesh.geometry);
    }
    if (mesh.userData.tileMaterial) {
      mesh.userData.tileMaterial.dispose();
    }
    if (mesh.userData.skirtMaterial) {
      mesh.userData.skirtMaterial.dispose();
    }
    mesh.userData.ownedGeometry = false;
    mesh.userData.geomKey = null;
    mesh.userData.colorKey = null;
    mesh.userData.tileMaterial = null;
    mesh.userData.skirtMaterial = null;
  }

  _isVisible(tileId, visibleSet) {
    for (const t of visibleSet) {
      if (t.id === tileId) return true;
    }
    return false;
  }
}

/**
 * Tiny LRU for displaced BufferGeometries. Map insertion order doubles as
 * LRU order: `take` removes the entry from the map (caller assumes ownership),
 * `put` always re-inserts at the tail. Evicted entries are disposed.
 */
class GeometryLRU {
  constructor(maxSize) {
    this.maxSize = maxSize;
    this._cache = new Map();
  }

  /** Returns and removes the cached geometry for `key`, or undefined. */
  take(key) {
    const geom = this._cache.get(key);
    if (geom === undefined) return undefined;
    this._cache.delete(key);
    return geom;
  }

  /** Hand `geom` to the cache (cache assumes ownership). */
  put(key, geom) {
    if (this._cache.has(key)) {
      // Same key already cached; the existing one is presumably equivalent.
      // Dispose the newcomer to avoid leaking.
      geom.dispose();
      return;
    }
    this._cache.set(key, geom);
    while (this._cache.size > this.maxSize) {
      const oldestKey = this._cache.keys().next().value;
      const oldest = this._cache.get(oldestKey);
      oldest.dispose();
      this._cache.delete(oldestKey);
    }
  }

  dispose() {
    for (const geom of this._cache.values()) geom.dispose();
    this._cache.clear();
  }
}
